import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  Assessment as StatsIcon,
  Clear as ClearIcon,
  Download as ExportIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { historyAPI, uploadAPI } from '../../services/api';

const QueryHistory = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Check if user has connections
  const { data: uploadStatus } = useQuery({
    queryKey: ['uploadStatus'],
    queryFn: () => uploadAPI.getUploadStatus(),
    retry: 1,
  });

  // Get query history
  const { data: queryHistory, isLoading } = useQuery({
    queryKey: ['queryHistory'],
    queryFn: () => historyAPI.getQueryHistory(),
    enabled: uploadStatus?.data?.has_connections,
  });

  // Get popular queries
  const { data: popularQueries } = useQuery({
    queryKey: ['popularQueries'],
    queryFn: () => historyAPI.getPopularQueries(),
    enabled: uploadStatus?.data?.has_connections,
  });

  // Get query stats
  const { data: queryStats } = useQuery({
    queryKey: ['queryStats'],
    queryFn: () => historyAPI.getQueryStats(),
    enabled: uploadStatus?.data?.has_connections,
  });

  // Delete query mutation
  const deleteQueryMutation = useMutation({
    mutationFn: (queryId) => historyAPI.deleteQuery(queryId),
    onSuccess: () => {
      toast.success('Query deleted');
      queryClient.invalidateQueries(['queryHistory']);
      queryClient.invalidateQueries(['popularQueries']);
    },
    onError: () => {
      toast.error('Failed to delete query');
    },
  });

  // Clear all queries mutation
  const clearHistoryMutation = useMutation({
    mutationFn: () => historyAPI.clearQueryHistory(),
    onSuccess: (response) => {
      toast.success(`Cleared ${response.data.deleted_count} queries`);
      queryClient.invalidateQueries(['queryHistory']);
      queryClient.invalidateQueries(['popularQueries']);
      queryClient.invalidateQueries(['queryStats']);
      setClearDialogOpen(false);
    },
    onError: () => {
      toast.error('Failed to clear history');
    },
  });

  // Export history mutation
  const exportMutation = useMutation({
    mutationFn: () => historyAPI.exportQueryHistory(),
    onSuccess: (response) => {
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `linkedin-search-history-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('History exported');
    },
    onError: () => {
      toast.error('Export failed');
    },
  });

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const hasConnections = uploadStatus?.data?.has_connections;

  if (!hasConnections) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Query History
        </Typography>
        
        <Alert severity="info">
          Upload your LinkedIn connections to start tracking your search history.
        </Alert>
      </Box>
    );
  }

  const renderHistoryTab = () => (
    <Box>
      {/* Header Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Search History ({queryHistory?.data?.pagination?.total || 0})
        </Typography>
        
        <Box display="flex" gap={1}>
          <Button
            startIcon={<ExportIcon />}
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            variant="outlined"
            size="small"
          >
            Export
          </Button>
          
          <Button
            startIcon={<ClearIcon />}
            onClick={() => setClearDialogOpen(true)}
            disabled={clearHistoryMutation.isPending}
            color="error"
            variant="outlined"
            size="small"
          >
            Clear All
          </Button>
        </Box>
      </Box>

      {/* History Table */}
      {queryHistory?.data?.queries?.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Query</TableCell>
                <TableCell align="center">Results</TableCell>
                <TableCell align="center">Time (ms)</TableCell>
                <TableCell align="center">Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queryHistory.data.queries.map((query) => (
                <TableRow key={query.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {query.query_text}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={query.results_count || 0}
                      size="small"
                      color={query.results_count > 0 ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {query.execution_time_ms?.toFixed(1) || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(query.created_at), 'MMM dd, HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => deleteQueryMutation.mutate(query.id)}
                      disabled={deleteQueryMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          No search history yet. Start searching to see your queries here.
        </Alert>
      )}
    </Box>
  );

  const renderPopularTab = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Popular Searches
      </Typography>
      
      {popularQueries?.data?.popular_queries?.length > 0 ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {popularQueries.data.popular_queries.map((query, index) => (
            <Card key={index}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">
                    {query.query_text}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      icon={<TrendingIcon />}
                      label={`${query.frequency}x`}
                      size="small"
                      color="primary"
                    />
                    <Button
                      size="small"
                      startIcon={<SearchIcon />}
                      variant="outlined"
                    >
                      Search Again
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <Alert severity="info">
          No popular searches yet. Your frequently used queries will appear here.
        </Alert>
      )}
    </Box>
  );

  const renderStatsTab = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Search Statistics
      </Typography>
      
      {queryStats?.data && (
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Overview Stats */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Overview
              </Typography>
              <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
                <Box>
                  <Typography variant="h4" color="primary">
                    {queryStats.data.total_queries}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Searches
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="primary">
                    {queryStats.data.recent_queries_30_days}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 30 Days
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="primary">
                    {queryStats.data.average_results_per_query}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Results/Query
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" color="primary">
                    {queryStats.data.average_execution_time_ms}ms
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Search Time
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Activity Chart */}
          {queryStats.data.query_frequency_7_days?.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📈 Recent Activity (Last 7 Days)
                </Typography>
                <Box display="flex" alignItems="end" gap={1} height={100}>
                  {queryStats.data.query_frequency_7_days.map((day, index) => (
                    <Box
                      key={index}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      flex={1}
                    >
                      <Box
                        sx={{
                          height: `${(day.count / Math.max(...queryStats.data.query_frequency_7_days.map(d => d.count))) * 80}px`,
                          minHeight: '4px',
                          width: '100%',
                          backgroundColor: 'primary.main',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(day.date), 'MMM dd')}
                      </Typography>
                      <Typography variant="caption" color="primary">
                        {day.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Query History & Analytics
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Track your search patterns and discover insights about your LinkedIn network queries.
      </Typography>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="fullWidth">
          <Tab icon={<HistoryIcon />} label="History" />
          <Tab icon={<TrendingIcon />} label="Popular" />
          <Tab icon={<StatsIcon />} label="Statistics" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {currentTab === 0 && renderHistoryTab()}
        {currentTab === 1 && renderPopularTab()}
        {currentTab === 2 && renderStatsTab()}
      </Box>

      {/* Clear Confirmation Dialog */}
      <Dialog open={clearDialogOpen} onClose={() => setClearDialogOpen(false)}>
        <DialogTitle>Clear Search History</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all your search history? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => clearHistoryMutation.mutate()}
            color="error"
            disabled={clearHistoryMutation.isPending}
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueryHistory;